/**
 * The one thing that notices a view has changed size — including the half of
 * "size" that a `ResizeObserver` cannot see.
 *
 * ## Why a `ResizeObserver` is not enough on its own
 *
 * A surface has **two** dimensions that matter and they move independently:
 *
 * - its **CSS box**, which is what a `ResizeObserver` reports, and
 * - its **`devicePixelRatio`**, which is how many device pixels each of those
 *   CSS pixels is drawn with.
 *
 * Dragging a window from a 1x monitor to a 2x one changes the second and not
 * the first. The element's `contentRect` is identical before and after, so
 * `ResizeObserver` **does not fire at all**, and every drawing buffer in the
 * app keeps the ratio it was built with — half the resolution the panel has,
 * for the rest of the session. Browser zoom is the same event from the other
 * direction. That was the state of this port before this module existed: 14
 * `ResizeObserver`s and not one reader of `devicePixelRatio` after boot
 * (`view-pixel-ratio.ts` is the *policy*; nothing re-applied it).
 *
 * ## How a ratio change is detected
 *
 * There is no `devicePixelRatio` event. The portable trick is a media query
 * pinned to the *current* ratio — `(resolution: 2dppx)` — which is matching by
 * construction and therefore fires `change` at the exact moment it stops being
 * true. It has to be **re-armed** against the new ratio each time, which is why
 * the listener below replaces its own query rather than keeping one.
 *
 * `resolution` is used rather than `-webkit-device-pixel-ratio` because it is
 * the standard form and every target of this port supports it; a browser
 * without `matchMedia` at all (and the test environment, which has no window)
 * degrades to size-only observation rather than throwing.
 *
 * ## Why the metrics are one object
 *
 * So that consumers cannot read the width from one moment and the ratio from
 * another. Every fan-out in `/fly` (`scene-boot.ts`'s `resizeViewport`) used to
 * re-read `container.clientWidth` for itself — four independent measurements of
 * one box, which is four chances to drift and four forced layouts. A single
 * measurement handed down as a value removes both.
 */

/** A view's size, as the two numbers that together decide every buffer sized against it. */
export interface ViewMetrics {
  /** The element's content box, in CSS pixels. Never below 1 — a collapsed box would size a buffer to zero. */
  readonly widthCssPx: number;
  readonly heightCssPx: number;
  /** Device pixels per CSS pixel, as the browser currently reports it. Never below or equal to zero. */
  readonly devicePixelRatio: number;
}

/** What `measureView` needs of an element — kept structural so tests need no DOM. */
export interface MeasurableView {
  readonly clientWidth: number;
  readonly clientHeight: number;
}

/**
 * `globalThis.devicePixelRatio` where there is a window, and 1 where there is
 * not (tests, workers) or where the browser reports nonsense.
 *
 * Deliberately the same guard `view-pixel-ratio.ts` applies for the same
 * reason: a `NaN` reaching a buffer size blanks the view rather than throwing,
 * which is the failure that is hardest to trace back to here.
 */
export function currentDevicePixelRatio(): number {
  const ratio = (globalThis as { devicePixelRatio?: number }).devicePixelRatio;
  return typeof ratio === "number" && Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
}

/** The element's current metrics. A zero or missing dimension reads as 1, never as 0. */
export function measureView(element: MeasurableView, devicePixelRatio = currentDevicePixelRatio()): ViewMetrics {
  const width = element.clientWidth;
  const height = element.clientHeight;
  return {
    widthCssPx: Number.isFinite(width) && width > 0 ? width : 1,
    heightCssPx: Number.isFinite(height) && height > 0 ? height : 1,
    devicePixelRatio: Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1,
  };
}

/** Whether two metrics describe the same view. All three fields, because a consumer exists for each. */
export function sameViewMetrics(a: ViewMetrics, b: ViewMetrics): boolean {
  return (
    a.widthCssPx === b.widthCssPx && a.heightCssPx === b.heightCssPx && a.devicePixelRatio === b.devicePixelRatio
  );
}

/**
 * The drawing-buffer size a view of these metrics wants, in device pixels.
 *
 * `Math.floor` rather than `Math.round` to match what three's `setSize` does
 * with the same two numbers, so a caller that sizes something alongside the
 * renderer gets the renderer's answer rather than one that is a pixel off at
 * fractional ratios (1.15 and 1.25 are both common on Windows).
 */
export function drawingBufferSize(metrics: ViewMetrics): { readonly width: number; readonly height: number } {
  return {
    width: Math.max(1, Math.floor(metrics.widthCssPx * metrics.devicePixelRatio)),
    height: Math.max(1, Math.floor(metrics.heightCssPx * metrics.devicePixelRatio)),
  };
}

/** The subset of `matchMedia` this module uses, so a test can supply one without a DOM. */
export interface ResolutionQuery {
  addEventListener(type: "change", listener: () => void): void;
  removeEventListener(type: "change", listener: () => void): void;
}

/** What `observeViewMetrics` needs of its environment — injected so the whole path is testable. */
export interface ViewMetricsEnvironment {
  observeResize(element: MeasurableView, onResize: () => void): () => void;
  /** Arms a one-shot watch for "the device pixel ratio is no longer `ratio`". `undefined` where the browser cannot. */
  watchResolution?(ratio: number, onChange: () => void): (() => void) | undefined;
  devicePixelRatio(): number;
}

/** The real browser environment. Falls back to size-only observation wherever a piece is missing. */
export function browserViewMetricsEnvironment(): ViewMetricsEnvironment {
  return {
    observeResize(element, onResize) {
      const observer = new ResizeObserver(() => onResize());
      // A real narrowing rather than a cast: `MeasurableView` is structural so
      // that tests can pass a plain object, and only a genuine `Element` can be
      // observed. Anything else falls through to the resolution watch alone,
      // which is the right degradation — a caller with no DOM node has no box
      // that can change.
      if (typeof Element !== "undefined" && element instanceof Element) observer.observe(element);
      return () => observer.disconnect();
    },
    watchResolution(ratio, onChange) {
      const media = (globalThis as { matchMedia?: (query: string) => ResolutionQuery }).matchMedia;
      if (typeof media !== "function") return undefined;
      // Pinned to the ratio that is true *now*, so the query is matching when it
      // is armed and firing when it stops being so. Re-armed by the caller.
      const query = media(`(resolution: ${ratio}dppx)`);
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    },
    devicePixelRatio: currentDevicePixelRatio,
  };
}

/**
 * Watches one element's metrics and calls back **once per actual change**.
 *
 * The de-duplication is load-bearing rather than an optimisation. A ratio
 * change fires both watches on most browsers — the media query, and a
 * `ResizeObserver` whose `devicePixelContentBoxSize` moved — and every consumer
 * downstream of this rebuilds a projection matrix or reallocates a swapchain.
 * Comparing against the last delivered metrics collapses that to one.
 *
 * Returns the disposer. It is safe to call twice.
 */
export function observeViewMetrics(
  element: MeasurableView,
  onChange: (metrics: ViewMetrics) => void,
  environment: ViewMetricsEnvironment = browserViewMetricsEnvironment(),
): () => void {
  let last = measureView(element, environment.devicePixelRatio());
  let disarmResolution = environment.watchResolution?.(last.devicePixelRatio, () => settle());
  let disposed = false;

  function settle(): void {
    if (disposed) return;
    const next = measureView(element, environment.devicePixelRatio());
    if (next.devicePixelRatio !== last.devicePixelRatio) {
      // Re-arm before the callback: a consumer that throws must not leave the
      // watch pinned to a ratio that is already false, or no later change on
      // this display is ever noticed again.
      disarmResolution?.();
      disarmResolution = environment.watchResolution?.(next.devicePixelRatio, () => settle());
    }
    if (sameViewMetrics(next, last)) return;
    last = next;
    onChange(next);
  }

  const disconnectResize = environment.observeResize(element, settle);
  return () => {
    if (disposed) return;
    disposed = true;
    disconnectResize();
    disarmResolution?.();
    disarmResolution = undefined;
  };
}
